import { trans } from "@mongez/localization";
import { Form } from "@mongez/react-form";
import { useRevalidator } from "@remix-run/react";
import { addressesAtom, useUpdateAddress } from "core/addresses";
import { checkoutAtom, useGuestSetShippingAddress } from "core/checkout";
import { createPortal } from "core/shared";
import { userAtom } from "core/user";
import {
  Button,
  errorToast,
  PhoneNumberInput,
  SubmitButton,
  successToast,
  SwitchInput,
  TextareaInput,
  TextInput,
} from "~/design-system/components";
import { Portal } from "~/modules/shared/components";
import { SelectAddressCityAndDistrict } from "../addresses/SelectAddressCityAndDistrict";

export const editAddressPortal = createPortal("edit-address-portal");

export function EditAddressPortal() {
  const { address } = editAddressPortal.useData();

  const user = userAtom.useValue();

  const { cart } = checkoutAtom.useValue();

  const cartIsPickup = cart?.shippingType === "pickup";

  const revalidator = useRevalidator();

  const { updateAddressSubmit } = useUpdateAddress(address?.id, {
    onSuccess: (response) => {
      successToast(trans("addressUpdatedSuccessfully"));
      addressesAtom.update(response.addresses);

      checkoutAtom.change("addresses", response.addresses);

      revalidator.revalidate();
      editAddressPortal.close();
    },
    onError: (error: any) => {
      errorToast(error);
    },
  });

  const { setGuestShippingAddress } = useGuestSetShippingAddress({
    onSuccess: (data) => {
      successToast(trans("addressAddedSuccessfully"));
      const address = data.cart.shippingAddress;
      const { shippingType } = data.cart;

      if (shippingType === "pickup") {
        checkoutAtom.merge({
          addresses: [],
          cart: data.cart,
        });
      } else {
        checkoutAtom.merge({
          addresses: [address],
          cart: data.cart,
        });
      }

      revalidator.revalidate();
      editAddressPortal.close();
    },
    onError: (error) => {
      errorToast(error);
    },
  });

  const guestHandler = async (props: any) => {
    const augmentedProps = cartIsPickup
      ? {
          ...props,
          values: { ...props.values, shippingType: "delivery" },
        }
      : props;
    return setGuestShippingAddress(augmentedProps);
  };

  if (!address) return null;

  return (
    <Portal portal={editAddressPortal} title={trans("editAddress")}>
      <Form
        onSubmit={
          user.userType === "user" ? updateAddressSubmit : guestHandler
        }
        className="flex flex-col gap-6"
      >
        <TextInput
          classes={{
            input: "bg-white/50",
          }}
          name="name"
          label={trans("name")}
          placeholder={trans("name")}
          defaultValue={address.name || user.name}
          required
        />

        <PhoneNumberInput
          id="phoneNumber"
          name="phoneNumber"
          minLength={11}
          classes={{
            input: "bg-white/50",
          }}
          label={trans("phoneNumber")}
          defaultValue={address.phoneNumber || user.phoneNumber}
          placeholder={trans("phoneNumber")}
          className="w-full"
          inputClassName="rounded-lg py-4 px-4"
          variant="filled"
          required
        />

        <TextareaInput
          classes={{
            input: "bg-white/50",
          }}
          name="address"
          label={trans("address")}
          placeholder={trans("address")}
          required
          defaultValue={address.address}
        />

        <SelectAddressCityAndDistrict withDefaultValue address={address} />

        <TextInput
          classes={{
            input: "bg-white/50",
          }}
          name="label"
          label={trans("addressLabel")}
          placeholder={trans("addressLabelHint")}
          defaultValue={address.label}
        />

        <TextInput
          classes={{
            input: "bg-white/50",
          }}
          name="landmark"
          label={trans("landmark")}
          placeholder={trans("landmark")}
          defaultValue={address.landmark}
        />
        <SwitchInput
          checked={address.isPrimary}
          name="isPrimary"
          label={trans("makePrimary")}
          color="secondary"
        />
        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={editAddressPortal.close}
            className="flex-1 justify-center rounded-full border bg-[#DFDFDF] px-6 py-3 font-medium hover:bg-[#d3d3d3]"
          >
            {trans("cancel")}
          </Button>
          <SubmitButton className="bg-primary-light hover:bg-primary-300 flex-1 justify-center rounded-full border px-6 py-3 font-medium">
            {trans("update")}
          </SubmitButton>
        </div>
      </Form>
    </Portal>
  );
}
